import React from "react";
import { useWindowDimensions } from "react-native";
import { MinusIcon, PlusIcon, XIcon } from "lucide-react-native";
import { useForm, useFormContext } from "react-hook-form";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnDatePickerInput } from "@/components/VnDatePickerInput/VnDatePickerInput";
import {
  VnDialog,
  VnDialogContent,
  VnDialogHeader,
  VnDialogPortal,
  VnDialogTitle,
} from "@/components/VnDialog";
import { VnForm } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { VnText } from "@/components/VnText/VnText";
import { MOBILE_BREAKPOINT } from "@/domain/constants";
import { HouseholdMemberType } from "@/domain/householdMember";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

type PregnancyFormType = {
  babiesExpected: number;
  dueDate: string | null;
};

type PregnancyDetailsPropsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: HouseholdMemberType | null;
  onSave: (babiesExpected: number, dueDate: string | null) => void;
};

type ContentPropsType = {
  memberName: string;
  handleSave: () => void;
};

const Content: React.FC<ContentPropsType> = ({ memberName, handleSave }) => {
  const { setValue, control, watch } = useFormContext<PregnancyFormType>();
  const babiesExpected = Number(watch("babiesExpected"));

  const handleIncrement = () => {
    const newValue = Math.min(babiesExpected + 1, 10);
    setValue("babiesExpected", newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max(babiesExpected - 1, 1);
    setValue("babiesExpected", newValue);
  };

  return (
    <VnBox className="gap-6">
      <VnBox className="flex flex-row items-center justify-between">
        <VnText variant="lg" className="text-muted-foreground">
          {memberName}
        </VnText>
      </VnBox>

      {/* Number of babies */}
      <VnBox className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <VnText
          variant="lgMedium"
          i18nKey="native:eligibilitySpecialConditionsScreen.pregnancyDetails.numberOfBabies"
        />
        <VnBox className="flex flex-row items-center gap-2 self-start">
          <VnButton
            variant="outline"
            size="icon"
            onPress={handleDecrement}
            isDisabled={babiesExpected <= 1}
            className="border-border rounded-full border p-2">
            <VnIcon as={MinusIcon} className="size-4" />
          </VnButton>
          <VnBox className="min-w-[60px] items-center justify-center">
            <VnText variant="lgBold">{babiesExpected}</VnText>
          </VnBox>
          <VnButton
            variant="outline"
            size="icon"
            onPress={handleIncrement}
            className="border-border rounded-full border p-2">
            <VnIcon as={PlusIcon} className="size-4" />
          </VnButton>
        </VnBox>
      </VnBox>

      {/* Estimated due date */}
      <VnBox className="flex flex-col gap-2">
        <VnText
          variant="lgMedium"
          i18nKey="native:eligibilitySpecialConditionsScreen.pregnancyDetails.estimatedDueDate"
        />
        <VnDatePickerInput
          name="dueDate"
          control={control}
          isRequired={false}
        />
      </VnBox>
      <VnBox className="flex flex-row justify-end">
        <VnButton
          onPress={handleSave}
          i18nKey="common:done"
          size="lg"
          className="w-[104px]"
        />
      </VnBox>
    </VnBox>
  );
};

export const PregnancyDetails: React.FC<PregnancyDetailsPropsType> = ({
  open,
  onOpenChange,
  member,
  onSave,
}) => {
  const { isWeb } = usePlatformOS();
  const { width } = useWindowDimensions();
  const isDesktopWeb = isWeb && width >= MOBILE_BREAKPOINT;

  const form = useForm<PregnancyFormType>({
    defaultValues: {
      babiesExpected: member?.pregnantBabiesExpected ?? 0,
      dueDate: member?.pregnantDueDate ?? undefined,
    },
  });

  const handleSave = () => {
    const formValues = form.getValues();
    onSave(Number(formValues.babiesExpected), formValues.dueDate);
    onOpenChange(false);
  };

  if (!member) {
    return null;
  }
  const memberName = formatHouseholdMemberName(member, "fullName");

  return (
    <VnForm form={form} isInputOnlyForm>
      {isDesktopWeb ? (
        <VnDialog open={open} onOpenChange={onOpenChange}>
          <VnDialogPortal>
            <VnDialogContent className="md:w-120 w-screen gap-2">
              <VnDialogHeader>
                <VnDialogTitle i18nKey="native:eligibilitySpecialConditionsScreen.pregnancyDetails.title" />
              </VnDialogHeader>
              <Content memberName={memberName} handleSave={handleSave} />
            </VnDialogContent>
          </VnDialogPortal>
        </VnDialog>
      ) : (
        <VnSheet
          open={open}
          onOpenChange={onOpenChange}
          side="bottom"
          shouldShowCloseButton={false}>
          <VnSheetContent className="bg-background">
            <VnBox className="flex flex-row items-center justify-between">
              <VnText
                variant="headerTwo"
                i18nKey="native:eligibilitySpecialConditionsScreen.pregnancyDetails.title"
                className="leading-none, whitespace-nowrap"
              />
              <VnButton
                hitSlop={12}
                onPress={() => onOpenChange(false)}
                variant="ghost">
                <VnIcon
                  as={XIcon}
                  className={cn(
                    "text-accent-foreground web:pointer-events-none size-5 shrink-0",
                  )}
                />
              </VnButton>
            </VnBox>
            <Content memberName={memberName} handleSave={handleSave} />
          </VnSheetContent>
        </VnSheet>
      )}
    </VnForm>
  );
};
